import "../../globals.scss";
import styles from "./RichTextEditor.module.scss";
import { EditorProvider, useCurrentEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import Link from "@tiptap/extension-link";
import { FocusEventHandler, ReactNode, useEffect, useState } from "react";
import { RichTextEditorToolbar } from "./RichTextEditorToolbar";
import { StatusText } from "../StatusText";

type RichTextEditorProps = {
  id?: string;
  label: string;
  hideLabel?: boolean;
  required?: boolean;
  placeholderNode?: ReactNode;
  placeholder?: string;
  dark?: boolean;
  content?: string;
  onChange: (content: string) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  compact?: boolean;
  append?: ReactNode;
  infoMessage?: string;
  textVariables?: string[];
  dangerText?: string;
  successText?: string;
  infoText?: string;
  warningText?: string;
};

const emptyPTags = "<p></p>";

export function RichTextEditor({
  id,
  label,
  hideLabel,
  required,
  placeholder,
  placeholderNode,
  dark,
  content = "",
  onChange,
  onBlur,
  compact,
  append,
  infoMessage,
  textVariables,
  dangerText,
  successText,
  infoText,
  warningText,
}: RichTextEditorProps) {
  const editorId = id ? id : `${label.replace(" ", "_")}_EDITOR`;
  const [cursorPosition, setCursorPosition] = useState<number | undefined>();
  const [hasFocus, setHasFocus] = useState(false);

  const extensions = [
    StarterKit,
    Placeholder.configure({
      placeholder: placeholder ? placeholder : "",
    }),
    Link.configure({
      protocols: ["ftp", "mailto"],
    }),
  ];

  return (
    <>
      <div
        className={`${styles.editorContainer} ${dark ? styles.dark : ""} ${dangerText ? styles.danger : ""} ${successText ? styles.success : ""}`}
      >
        <label
          htmlFor={editorId}
          className={hideLabel || (compact && hideLabel) ? "sr-only" : ""}
        >
          {label}
          {required && (
            <>
              {" "}
              <strong aria-hidden>*</strong>
              <span className="sr-only">(required)</span>
            </>
          )}
        </label>
        <EditorProvider
          shouldRerenderOnTransaction={true}
          editorContainerProps={{
            onFocus: () => setHasFocus(true),
            onBlur: (e) => {
              setHasFocus(false);
              onBlur?.(e);
            },
          }}
          editorProps={{
            attributes: {
              id: editorId,
              "data-invalid": dangerText ? "true" : "false",
            },
          }}
          slotBefore={
            <RichTextEditorToolbar
              editorId={editorId}
              infoMessage={infoMessage}
              textVariables={textVariables}
              cursorPosition={cursorPosition}
              placeholderNode={
                !hasFocus &&
                (!content || content === emptyPTags) &&
                placeholderNode
              }
            />
          }
          slotAfter={<ContentSync content={content} />}
          extensions={extensions}
          content={content}
          onSelectionUpdate={(editorContent) =>
            setCursorPosition(editorContent.editor.state.selection.anchor)
          }
          onUpdate={(editorContent) => {
            const html = editorContent.editor.getHTML();
            if (html === emptyPTags) {
              onChange("");
            } else {
              onChange(editorContent.editor.getHTML());
            }
          }}
        ></EditorProvider>
      </div>
      <div className={styles.bottomContainer}>
        {append}
        {!dangerText &&
        !successText &&
        !warningText &&
        !infoText &&
        !compact ? (
          <div style={{ height: "24px" }}></div>
        ) : dangerText ? (
          <StatusText text={dangerText} fail dark={dark} />
        ) : successText ? (
          <StatusText text={successText} success dark={dark} />
        ) : warningText ? (
          <StatusText text={warningText} warning dark={dark} />
        ) : (
          !!infoText && <StatusText text={infoText} dark={dark} />
        )}
      </div>
    </>
  );
}

// Required to access the editor instance and sync content if it changes externally
const ContentSync = ({ content }: { content: string }) => {
  const { editor } = useCurrentEditor();

  useEffect(() => {
    if (!editor) return;

    // Only set content if it's different from what's already rendered
    if (content && editor.getHTML() !== content) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  return null;
};
